import { motion } from "framer-motion";
import { Reveal, GradientButton } from "@/components/common";
import { Check, ArrowRight, Home, Film, TrendingDown } from "lucide-react";

// Shared pricing card — used identically on the public homepage (Pricing.jsx)
// and inside the authenticated dashboard. Single source of truth for styling.
// `ctaLabel` / `ctaTestId` optionally override the button text / test id.
export function PriceCard({ pkg, index = 0, onSelect, ctaLabel, ctaTestId, disabled = false, vatLabel = "excl. btw" }) {
  const rec = pkg.recommended;
  const label = ctaLabel || pkg.cta;
  const testid = ctaTestId || `pricing-cta-${pkg.id}`;

  const inner = (
    <div className={`relative flex h-full flex-col rounded-[calc(1.75rem-2px)] p-8 ${rec ? "bg-[#0e0e11]" : "bg-white"}`}>
      {pkg.badge && (
        <span
          className={`absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-[11px] font-bold uppercase tracking-wider ${
            rec ? "bg-gradient-red text-white shadow-red-glow" : "border border-re-line bg-white text-re-charcoal"
          }`}
        >
          {pkg.badge}
        </span>
      )}

      {/* 1. name  2. audience */}
      <h3 className={`font-heading text-xl font-bold ${rec ? "text-white" : "text-re-ink"}`}>{pkg.name}</h3>
      <p className={`mt-2 text-sm ${rec ? "text-white/55" : "text-re-muted"}`}>{pkg.audience}</p>

      {/* 3. price 4. per maand + price per listing */}
      <div className="mt-6 flex items-end gap-1">
        <span className={`font-heading text-5xl font-black tracking-tight ${rec ? "text-white" : "text-re-ink"}`}>{pkg.price}</span>
        <span className={`mb-1.5 text-sm ${rec ? "text-white/50" : "text-re-muted"}`}>{pkg.period}</span>
      </div>
      <p className={`mt-1 text-sm font-medium ${rec ? "text-white/70" : "text-re-charcoal"}`}>{pkg.perProperty}</p>
      <p className={`mt-1 text-xs ${rec ? "text-white/45" : "text-re-muted"}`} data-testid={`pricing-vat-note-${pkg.id}`}>{vatLabel}</p>

      {/* monthly video options highlight */}
      <div className={`mt-5 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${rec ? "bg-white/10 text-white" : "bg-re-soft text-re-ink"}`}>
        <Film className={`h-4 w-4 ${rec ? "text-re-crimson" : "text-re-red"}`} />
        {pkg.monthlyOptions}
      </div>

      {/* feature list */}
      <ul className={`mt-6 space-y-3 border-t pt-6 ${rec ? "border-white/10" : "border-re-line"}`}>
        {pkg.features.map((f, k) => (
          <li key={f} className="flex items-start gap-2.5">
            <span className={`mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full ${k === 0 ? "bg-gradient-red" : rec ? "bg-white/10" : "bg-re-soft"}`}>
              {k === 0
                ? <Home className="h-3 w-3 text-white" strokeWidth={2.5} />
                : <Check className={`h-3 w-3 ${rec ? "text-re-crimson" : "text-re-red"}`} strokeWidth={3} />}
            </span>
            <span className={`text-sm ${k === 0 ? "font-semibold" : ""} ${rec ? "text-white" : "text-re-charcoal"}`}>{f}</span>
          </li>
        ))}
      </ul>

      {/* value / price advantage highlight */}
      {(pkg.value || pkg.highlight) && (
        <div className="mt-4 space-y-2">
          {pkg.value && (
            <div className="flex items-center gap-2 rounded-lg bg-gradient-red px-3 py-2 text-xs font-semibold text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} /> {pkg.value}
            </div>
          )}
          {pkg.highlight && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold ${rec ? "bg-white/10 text-white" : "bg-re-red/5 text-re-red"}`}>
              <TrendingDown className="h-3.5 w-3.5" /> {pkg.highlight}
            </div>
          )}
        </div>
      )}

      {/* 8. CTA */}
      <div className="mt-auto pt-8">
        {rec ? (
          <GradientButton testid={testid} onClick={onSelect} disabled={disabled} className={`w-full ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
            {label} <ArrowRight className="h-4 w-4" />
          </GradientButton>
        ) : (
          <button
            onClick={onSelect}
            data-testid={testid}
            disabled={disabled}
            className="group w-full rounded-full border border-re-line px-6 py-3.5 text-sm font-semibold text-re-ink transition-all hover:border-transparent hover:bg-gradient-red hover:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-re-ink disabled:hover:border-re-line"
          >
            <span className="inline-flex items-center gap-2">{label} <ArrowRight className="h-4 w-4" /></span>
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Reveal delay={index * 0.1} className={rec ? "md:-my-4 md:scale-[1.05] md:z-10" : ""}>
      <div className="relative h-full" data-testid={`pricing-card-${pkg.id}`}>
        {rec && <div className="pointer-events-none absolute -inset-3 rounded-[2rem] bg-gradient-red opacity-25 blur-2xl" />}
        {rec ? (
          <motion.div whileHover={{ y: -4 }} className="relative h-full rounded-[1.75rem] bg-gradient-red p-[2px] shadow-red-glow">
            {inner}
          </motion.div>
        ) : (
          <motion.div whileHover={{ y: -4 }} className="relative h-full rounded-[1.75rem] border border-re-line shadow-sm">
            {inner}
          </motion.div>
        )}
      </div>
    </Reveal>
  );
}
